import * as React from "react";
import { DeleteCompanyProfile, getCompanyProfileByUserId, getAllCompanyList, DeleteCompanyProfile_2 } from "./helper";
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  DeleteFolderIcon,
  FolderIcon,
  RenameFolderIcon,
} from "../../assets/FileRepositoryIcon";
import { companyList, DeleteCompany } from "./modal";
import { useUserContext } from "../../context/auth/UserContext";
import { useCompanyProfileContext } from "../../context/CompanyProfile/CompanyProfileContext";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import AuthContext from "../../context/auth/AuthContext";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import { useAccessRights } from "../../utils/useAccessRights";

export const DisplayCompaniesList = () => {
  const hasFetched = useRef(false);
  const companyProfilePageAccess = useAccessRights("User Configuration")
  const [openSnack, setOpenSnack] = React.useState(false);
  const [companyList, setCompanyList] = useState<companyList[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [deleteItem, setDeleteItem] = useState<companyList>();

  const { companyProfileID, userId, setCompanyProfileList, userAccess } = useUserContext();
  const authContext = React.useContext(AuthContext);
  const { portalUser } = authContext;
  const {
    setSelectedCompany,
    selectedCompany,
    refreshTable,
    setRefreshTable,
    isListLoading,
    setIsListLoading,
  } = useCompanyProfileContext();

  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
    openConfirmationDialog: false,
  });

  const fetchCompaniesList = async () => {
    try {
      setIsListLoading(true);
      let res = null
      if (userAccess.roles[0].roleName === "JSIAdmin" || userAccess.roles[0].roleName === "JSIUser") {
        res = await getAllCompanyList();
      } else {
        res = await getCompanyProfileByUserId(userId);
      }
      setCompanyProfileList(res);
      setCompanyList(res);
    } finally {
      setIsListLoading(false);
      setSelectedCompany(null);
      setRefreshTable(false);
    }
  };

  const closePreview = () => {
    setOpenDialog(false);
  };

  const showPreview = (item: any) => {
    setLogoPreview(item.logo);
    setOpenDialog(true);
  };

  const handleEdit = (item: any) => {
    setSelectedCompany(item);
  };
  const handleDelete = (item: companyList) => {
    if (item.companyProfileID === companyProfileID) {
      setOpenSnack(true);
      return;
    }
    setDeleteItem(item);
    setDialogState({
      title: "Confirm",
      body: `Are you sure you want to delete <strong>${item.companyName}</strong> ?`,
      action: "OK",
      actionCancel: "CANCEL",
      openConfirmationDialog: true,
      openSuccessDialogBox: false,
    });
  };

  const handleDeleteConfirmed = async () => {
    setDialogState({
      ...dialogState,
      openConfirmationDialog: false,
      openSuccessDialogBox: false,
    });
    try {
      const date = new Date();
      setIsListLoading(true);

      const payload: DeleteCompany = {
        companyProfileID:
          deleteItem && deleteItem.companyProfileID
            ? deleteItem.companyProfileID
            : 0,
        isDeleted: true,
        updateBy: userId,
        updateDate: date,
      };
      const res = await DeleteCompanyProfile(payload);
      if (res) {
        await DeleteCompanyProfile_2(deleteItem?.companyProfileID || 0);
        // console.log("handleDeleteFilesApi payload", selectedFile);
        fetchCompaniesList();

        setDialogState({
          title: "Success",
          body: `Succesfully deleted <strong>${deleteItem?.companyName}<strong>`,
          action: "OK",
          actionCancel: "",
          openConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setIsListLoading(false);
        setDialogState({
          title: "Error",
          body: `An error occurred while deleting the <strong>${deleteItem?.companyName}<strong>.`,
          action: "OK",
          actionCancel: "",
          openConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setSelectedCompany(null);
    }
  };

  const handleCloseDialog = () => {
    setDialogState({
      ...dialogState,
      openConfirmationDialog: false,
      openSuccessDialogBox: false,
    });
  };
  const handleCloseSnak = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnack(false);
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchCompaniesList();
      hasFetched.current = true;
    }
  }, []);

  useEffect(() => {
    if (refreshTable) {
      fetchCompaniesList();
    }
  }, [refreshTable]);

  return (
    <React.Fragment>
      <List
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          overflow: "auto",
          paddingY: "2px",

          flexGrow: 1,
          maxHeight: "100%",
          // Ensure it doesn't overflow
          "&::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "##FFFFFF",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "10px",
            border: "2px solid #f1f1f1",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        }}
      >
        {isListLoading ? (
          <ListItem
            sx={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <CircularProgress />
          </ListItem>
        ) : (
          <Grid direction="column">
            <Grid
              sx={{
                width: "100%",
                paddingY: "8px",
                bgcolor: "#f5f5f5", // Light gray background for the header
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)", // Subtle shadow for elevation
              }}
            >
              <Stack direction="row" justifyContent="space-evenly" width="100%">
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#333", // Dark text color
                    fontSize: "1.1rem", // Slightly larger font
                    width: "40%",
                    ml: 2,
                  }}
                >
                  Company Name
                </Typography>
              </Stack>
            </Grid>
            <Grid>
              {companyList && companyList.length > 0 ? (
                companyList.map((item, index) => (
                  <ListItem sx={{ paddingY: "4px" }} key={index}>
                    <Grid
                      container
                      flexGrow={1}
                      spacing={1}
                      mt={1}
                      alignItems="center"
                    >
                      <Grid xs={10}>

                        <ListItemText primary={item.companyName} />

                      </Grid>


                      <Grid xs={1}>
                        <ListItemButton
                          onClick={() => handleEdit(item)}
                          sx={{
                            "&:hover": {
                              bgcolor: "#E5F0FF",
                            },
                            borderRadius: "8px",
                            justifyContent: "center",
                          }}
                          disabled={!companyProfilePageAccess?.isUpdate}
                        >
                          <ListItemIcon sx={{ justifyContent: "center" }}>
                            {React.createElement(RenameFolderIcon)}
                          </ListItemIcon>
                        </ListItemButton>
                      </Grid>
                      <Grid xs={1}>
                        <ListItemButton
                          sx={{
                            "&:hover": {
                              bgcolor: "#E5F0FF",
                            },
                            borderRadius: "8px",
                            justifyContent: "center",
                          }}
                          onClick={() => handleDelete(item)}
                          disabled={!companyProfilePageAccess?.isDelete}

                        >
                          <ListItemIcon sx={{ justifyContent: "center" }}>
                            {React.createElement(DeleteFolderIcon)}
                          </ListItemIcon>
                        </ListItemButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    sx={{ opacity: 0.7, fontWeight: 400, fontStyle: "italic" }}
                  >
                    No companies are there under
                    <strong>
                      {`${portalUser?.firstname} ${portalUser?.lastname}`}
                    </strong>
                  </ListItemText>
                </ListItem>
              )}
            </Grid>
          </Grid>
        )}
      </List>

      {/* Dialog for Logo Preview */}
      <Dialog open={openDialog} onClose={closePreview}>
        <DialogTitle>Logo Preview</DialogTitle>
        <DialogContent>
          {logoPreview && (
            <img
              src={logoPreview}
              alt="Logo Preview"
              style={{ maxWidth: "100%" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {
        //Delete Conformation
        <DialogBoxComponent
          openDialogBox={dialogState.openConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogBody={dialogState.body}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          handleFunction={handleDeleteConfirmed}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //Delete Conformation
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogBody={dialogState.body}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          handleFunction={handleCloseDialog}
        />
      }
      <div>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={openSnack}
          autoHideDuration={6000}
          onClose={handleCloseSnak}
        >
          <Alert
            onClose={handleCloseSnak}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Selected company cannot be deleted. Please switch to another company
            to delete.{" "}
          </Alert>
        </Snackbar>
      </div>
    </React.Fragment>
  );
};
